import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

export interface AirportOption {
	iata: string;
	name: string;
}

export interface AirportAutocompleteProps {
	value?: string;
	onChange: (iata: string) => void;
	onSearch: (query: string) => Promise<AirportOption[]>;
	placeholder?: string;
	debounceMs?: number;
	minChars?: number;
	disabled?: boolean;
	className?: string;
}

export function AirportAutocomplete({
	value = '',
	onChange,
	onSearch,
	placeholder = 'Airport...',
	debounceMs = 250,
	minChars = 2,
	disabled = false,
	className,
}: AirportAutocompleteProps) {
	const [inputValue, setInputValue] = useState(value);
	const [suggestions, setSuggestions] = useState<AirportOption[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	useEffect(() => { setInputValue(value); }, [value]);

	// Position the dropdown relative to the input
	const updatePosition = useCallback(() => {
		if (!inputRef.current) return;
		const rect = inputRef.current.getBoundingClientRect();
		setDropdownPos({
			top: rect.bottom + 4,
			left: rect.left,
		});
	}, []);

	useEffect(() => {
		if (isOpen) {
			updatePosition();
			window.addEventListener('scroll', updatePosition, true);
			window.addEventListener('resize', updatePosition);
			return () => {
				window.removeEventListener('scroll', updatePosition, true);
				window.removeEventListener('resize', updatePosition);
			};
		}
	}, [isOpen, updatePosition]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				containerRef.current && !containerRef.current.contains(target) &&
				dropdownRef.current && !dropdownRef.current.contains(target)
			) {
				setIsOpen(false);
			}
		};
		if (isOpen) document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const fetchSuggestions = async (query: string) => {
		if (query.length < minChars) {
			setSuggestions([]);
			setIsOpen(false);
			return;
		}
		setIsLoading(true);
		try {
			const results = await onSearch(query);
			setSuggestions(results);
			setIsOpen(results.length > 0);
		} catch {
			setSuggestions([]);
		} finally {
			setIsLoading(false);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const v = e.target.value.toUpperCase();
		setInputValue(v);
		setHighlightedIndex(-1);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => fetchSuggestions(v), debounceMs);
	};

	const handleSelect = (item: AirportOption) => {
		setInputValue(item.iata);
		onChange(item.iata);
		setIsOpen(false);
		setSuggestions([]);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!isOpen) {
			if (e.key === 'ArrowDown' && suggestions.length > 0) { e.preventDefault(); setIsOpen(true); }
			return;
		}
		switch (e.key) {
			case 'ArrowDown': e.preventDefault(); setHighlightedIndex(p => Math.min(p + 1, suggestions.length - 1)); break;
			case 'ArrowUp': e.preventDefault(); setHighlightedIndex(p => Math.max(p - 1, -1)); break;
			case 'Enter':
				e.preventDefault();
				if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) handleSelect(suggestions[highlightedIndex]);
				else if (inputValue) { onChange(inputValue); setIsOpen(false); }
				break;
			case 'Escape': e.preventDefault(); setIsOpen(false); break;
		}
	};

	const handleBlur = () => {
		if (inputValue !== value) onChange(inputValue);
	};

	const dropdown = isOpen && suggestions.length > 0 && dropdownPos && createPortal(
		<div
			ref={dropdownRef}
			className="fixed z-[9999] w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto"
			style={{ top: dropdownPos.top, left: dropdownPos.left }}
		>
			{suggestions.map((item, i) => (
				<button
					key={item.iata}
					type="button"
					onClick={() => handleSelect(item)}
					onMouseEnter={() => setHighlightedIndex(i)}
					className={cn(
						'w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2',
						highlightedIndex === i && 'bg-gray-50',
					)}
				>
					<span className="font-mono font-semibold text-emerald-700 shrink-0">{item.iata}</span>
					<span className="text-gray-500 truncate text-xs">{item.name}</span>
				</button>
			))}
		</div>,
		document.body,
	);

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			<input
				ref={inputRef}
				type="text"
				value={inputValue}
				onChange={handleInputChange}
				onKeyDown={handleKeyDown}
				onBlur={handleBlur}
				onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
				placeholder={placeholder}
				disabled={disabled}
				className="w-full px-1.5 py-0.5 text-sm border rounded font-mono uppercase focus:outline-none focus:ring-1 focus:ring-blue-500"
				autoFocus
			/>
			{isLoading && (
				<div className="absolute right-2 top-1/2 -translate-y-1/2">
					<div className="w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
				</div>
			)}
			{dropdown}
		</div>
	);
}
